import { Flex } from 'antd';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Spin } from '../Ant';
import { useInView } from 'react-intersection-observer';
import NoData from '../dashboard/noData';
import { ReceiptEmpty } from '../icons';
import SearchInputWithFilters from '../dashboard/SearchInputWithFilters';
import InvoiceMatterCard from './invoiceMatterCard';
import { useFetchPreliminaryInvoices } from '../../hooks/useGetFetchPreliminaryInvoices';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { ROUTES } from '../../constants/constants';
import { useSearchParams } from 'react-router-dom';
import { IPreliminaryInvoice, IPreliminaryInvoiceV2 } from '../../interface/invoice';
import { useSystemInfo } from '../../context/SystemInfoContext';
import SortSelector from '../sortPopover/sortSelector';
import { IActiveSortState, ISortOption } from '../../interface/common';
import SkeletonCardList from '../common/skeleton/skeletonCardList';
import CollapsibleListLayout from '../collapsibleListLayout/collapsibleListLayout';

const PreliminaryInvoiceListing = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { push } = useNavigationStack();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get('preliminarySearch') || '';
  const [searchValue, setSearchValue] = useState<string>(initialSearch);
  const { systemParams } = useSystemInfo();
  const isPreInvoiceV2 = systemParams?.featureFlags?.preliminaryInvoice?.version === 'v2';
  const prelimInvoiceSortOptions: ISortOption[] = useMemo(
    () => [
      {
        field: 'entryDate',
        label: t('preliminaryInvoice:entryDate'),
        type: 'date',
        defaultDirection: 'DESC',
      },
      { field: 'matter.subject', label: t('matter:matter'), type: 'text', defaultDirection: 'ASC' },
    ],
    [t],
  );

  const [activeSort, setActiveSort] = useState<IActiveSortState>({
    field: 'entryDate',
    direction: 'DESC',
  });
  const {
    refetch,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    data: preliminaryInvoices,
    isLoading: isPreliminaryInvoicesLoading,
  } = useFetchPreliminaryInvoices({
    search: searchValue,
    limit: 10,
    sortBy: [`${activeSort.field}:${activeSort.direction}`],
    relations: [
      'matter:invoiceableType,subject,matterNumber,currency',
      'company:name',
      'privatePerson:fullName',
    ],
    fields: [
      'status',
      'entryDate',
      isPreInvoiceV2 ? 'totalNetAmount' : 'sumOfBillableEntries',
      isPreInvoiceV2 ? 'totalNetAmountInMatterCurrency' : 'sumOfBillableEntriesInCurrency',
      'periodStartDate',
      'periodEndDate',
      'reviewStatus',
      'assignToUserId',
      'hasImportantNotes',
      'hasImportantRelatedNotes',
      'hasNotes',
    ],
  });
  useEffect(() => {
    setSearchParams({
      preliminarySearch: searchValue,
    });
  }, [searchValue]);
  const handleGotoPreliminaryInvoiceDetail = (invoiceId: string) => {
    push(ROUTES.PRELIMINARY_INVOICE_DETAIL + `/${invoiceId}`);
  };
  const { ref, inView } = useInView({
    threshold: 0.5,
  });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && preliminaryInvoices?.length > 0) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage]);
  const renderLoader = () => {
    if (isFetching) {
      return <Spin />;
    }
    return null;
  };
  return (
    <CollapsibleListLayout
      testId="preliminary-invoice-listing"
      header={
        <SearchInputWithFilters
          searchTitle={t('preliminaryInvoice:search')}
          handleRefresh={() => {
            queryClient.removeQueries({
              queryKey: ['preliminary-invoices-list'],
            });
            return refetch();
          }}
          handleSearch={(val: string) => {
            setSearchValue(val);
          }}
          searchValue={searchValue}
        >
          <SortSelector
            modalLabel={t('common:sorting.title')}
            options={prelimInvoiceSortOptions}
            onApply={(sort) => setActiveSort(sort)}
            activeSort={activeSort}
          />
        </SearchInputWithFilters>
      }
    >
      {preliminaryInvoices &&
        preliminaryInvoices.length > 0 &&
        preliminaryInvoices.map((item) => {
          return (
            <InvoiceMatterCard
              key={item?.id}
              invoiceStatus={item?.status}
              subject={item?.matter?.subject}
              matterInvoiceableType={item?.matter?.invoiceableType}
              number={item?.matter?.matterNumber}
              customer={item?.company?.name ?? item?.privatePerson?.fullName ?? ''}
              startDate={item?.periodStartDate}
              endDate={item?.periodEndDate}
              amount={
                isPreInvoiceV2
                  ? ((item as IPreliminaryInvoiceV2).totalNetAmount ?? 0)
                  : ((item as IPreliminaryInvoice).sumOfBillableEntries ?? 0)
              }
              amountInCurrency={
                isPreInvoiceV2
                  ? ((item as IPreliminaryInvoiceV2).totalNetAmountInMatterCurrency ?? 0)
                  : ((item as IPreliminaryInvoice).sumOfBillableEntriesInCurrency ?? 0)
              }
              currency={item?.matter?.currency}
              hasImportantNotes={item?.hasImportantNotes}
              hasImportantRelatedNotes={item?.hasImportantRelatedNotes}
              hasNotes={item?.hasNotes}
              onSelect={() => handleGotoPreliminaryInvoiceDetail(item?.id)}
            />
          );
        })}
      {hasNextPage && <div ref={ref} className="loading-indicator" />}
      {(!preliminaryInvoices ||
        (Object.entries(preliminaryInvoices).length === 0 && !isPreliminaryInvoicesLoading)) && (
        <Flex className="flex-grow" justify="center" align="center">
          <NoData header={t('preliminaryInvoice:noPreliminaryInvoices')} icon={<ReceiptEmpty />} />
        </Flex>
      )}
      {isPreliminaryInvoicesLoading ? (
        <SkeletonCardList rows={[1, 1, 2]} withSubcontentOnFirstRow />
      ) : (
        renderLoader()
      )}
    </CollapsibleListLayout>
  );
};

export default PreliminaryInvoiceListing;
